import Phaser from 'phaser';
import { TextureKey } from '../assets/manifest.js';
import { TUNING } from '../config/tuning.js';
import type { WorldScale } from '../core/WorldScale.js';
import { CSS, PALETTE, UI_FONT, UI_HEAVY } from '../ui/style.js';

const K = TUNING.checkpoint;

/**
 * Subsidy checkpoint: a plane pulls a banner ("180 € staatliche Zulage erreicht!") once across the playfield,
 * from right to left. While it flies no new coins appear (see PlayScene).
 */
export class CheckpointBanner {
  private readonly container: Phaser.GameObjects.Container;
  private readonly text: Phaser.GameObjects.Text;
  private readonly cloth: Phaser.GameObjects.Graphics;
  private readonly rope: Phaser.GameObjects.Graphics;
  private readonly planeW: number;
  private flying = false;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly ws: WorldScale,
    private readonly viewWidth: number,
    depth: number,
  ) {
    const px = (wu: number) => Math.round(ws.px(wu));
    const plane = scene.add.image(0, 0, TextureKey.Plane).setOrigin(0, 0.5).setDisplaySize(px(192), px(80));
    this.planeW = plane.displayWidth;
    this.rope = scene.add.graphics();
    this.cloth = scene.add.graphics();
    this.text = scene.add
      .text(0, 0, '', { fontFamily: UI_FONT, fontSize: `${px(40)}px`, fontStyle: UI_HEAVY, color: CSS.ink })
      .setOrigin(0, 0.5);
    this.container = scene.add.container(0, 0, [this.rope, this.cloth, this.text, plane]).setDepth(depth).setVisible(false);
  }

  get active(): boolean {
    return this.flying;
  }

  show(label: string): void {
    const px = (wu: number) => this.ws.px(wu);
    this.text.setText(label);
    // banner behind the tail: rope, then the cloth with the text
    const gap = px(46);
    const padX = px(22);
    const h = this.text.height + px(16);
    const x0 = this.planeW + gap;
    const w = this.text.width + padX * 2;
    this.rope.clear().lineStyle(Math.max(1, px(4)), PALETTE.ink, 1).lineBetween(this.planeW - px(8), 0, x0, 0);
    this.cloth.clear();
    this.cloth.fillStyle(PALETTE.ink, 1).fillRect(x0 - px(4), -h / 2 - px(4), w + px(8), h + px(8));
    this.cloth.fillStyle(PALETTE.orange, 1).fillRect(x0, -h / 2, w, h);
    this.cloth.fillStyle(PALETTE.orangeHi, 1).fillRect(x0, -h / 2, w, px(6));
    this.text.setPosition(x0 + padX, 0);

    const total = x0 + w;
    const y = this.ws.pixelHeight * K.yFraction;
    this.scene.tweens.killTweensOf(this.container);
    this.flying = true;
    this.container.setPosition(this.viewWidth, y).setVisible(true);
    this.scene.tweens.add({
      targets: this.container,
      x: -total,
      duration: K.bannerSec * 1000,
      ease: 'Linear',
      onUpdate: () => (this.container.y = y + Math.sin(this.scene.time.now / 260) * px(8)),
      onComplete: () => this.hide(),
    });
  }

  hide(): void {
    this.scene.tweens.killTweensOf(this.container);
    this.container.setVisible(false);
    this.flying = false;
  }
}
